import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIsTransport } from '../hooks/useIsTransport';
import { companyApi } from '../api/company';
import type { AppSettings, InvoiceSequenceDto } from '../api/company';
import { downloadBackup } from '../api/attachments';
import { API_BASE } from '../api/client';
import type { CompanyProfile } from '@coreledger/shared-types';
import { confirm } from '../components/ConfirmDialog';
import { useCloudSync } from '../hooks/useSync';

const IS_DESKTOP = !!import.meta.env.VITE_API_URL;

const GST_RATES = [1, 2, 5];
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh'];
const empty = (): CompanyProfile => ({ name: '', country: 'India' });

const DOC_TYPE_LABELS: Record<string, string> = {
  TAX_INVOICE: 'Tax Invoice',
  BILL_OF_SUPPLY: 'Bill of Supply',
  QUOTATION: 'Quotation',
  CREDIT_NOTE: 'Credit Note',
  DEBIT_NOTE: 'Debit Note',
  LORRY_RECEIPT: 'Lorry Receipt',
};
const INVOICE_FORMATS = [
  { key: 'classic',  label: 'Classic',  desc: 'Traditional blue header' },
  { key: 'modern',   label: 'Modern',   desc: 'Dark slate with accents' },
  { key: 'minimal',  label: 'Minimal',  desc: 'No-frills, black & white' },
  { key: 'elegant',  label: 'Elegant',  desc: 'Green professional style' },
  { key: 'bold',     label: 'Bold',     desc: 'Amber accent, high contrast' },
] as const;

const emptySettings = (): AppSettings => ({
  currency: 'INR', dateFormat: 'DD/MM/YYYY',
  defaultTaxType: 'cgst_sgst', defaultDocType: 'tax_invoice',
  termsAndConditions: '', invoiceFooterNote: '', enableStock: false,
  invoiceFormat: localStorage.getItem('cl_invoice_format') ?? 'classic',
});
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];
const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const DOC_TYPES = [
  { value: 'tax_invoice', label: 'Tax Invoice' },
  { value: 'bill_of_supply', label: 'Bill of Supply' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'lorry_receipt', label: 'Lorry Receipt' },
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const isTransport = useIsTransport();
  const { isConnected, isSyncing, pendingCount, lastSyncAt, syncError, conflictCount, connectToCloud, sync, disconnect } = useCloudSync();
  const [cloudUrl, setCloudUrl] = useState('');
  const [cloudEmail, setCloudEmail] = useState('');
  const [cloudPassword, setCloudPassword] = useState('');
  const [connectingCloud, setConnectingCloud] = useState(false);
  const { data: profile, isLoading } = useQuery({ queryKey: ['profile'], queryFn: companyApi.get, staleTime: 30 * 1000 });
  const { data: appSettings } = useQuery({ queryKey: ['app-settings'], queryFn: companyApi.getSettings, staleTime: 30 * 1000 });
  const [form, setForm] = useState<CompanyProfile>(empty());
  const [settingsForm, setSettingsForm] = useState<AppSettings>(emptySettings());
  const [settingsMsg, setSettingsMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [watermark, setWatermark] = useState(() => localStorage.getItem('cl_watermark') ?? '');
  const { data: sequences = [] } = useQuery({ queryKey: ['sequences'], queryFn: companyApi.getSequences, staleTime: 30 * 1000 });
  const [seqPrefixes, setSeqPrefixes] = useState<Record<string, string>>({});
  const [seqMsg, setSeqMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (appSettings) {
      setSettingsForm({ ...emptySettings(), ...appSettings });
      if (appSettings.invoiceFormat) localStorage.setItem('cl_invoice_format', appSettings.invoiceFormat);
    }
  }, [appSettings]);

  useEffect(() => {
    if (sequences.length) {
      const map: Record<string, string> = {};
      sequences.forEach(s => { map[s.docType] = s.prefix; });
      setSeqPrefixes(map);
    }
  }, [sequences]);

  useEffect(() => { if (profile) setForm(profile); }, [profile]);

  const deleteProfile = useMutation({
    mutationFn: () => companyApi.deleteProfile(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['company-profile'] });
      qc.refetchQueries({ type: 'all' });
      setForm(empty());
    },
    onError: err => setMsg({ type: 'err', text: err instanceof Error ? err.message : 'Delete failed' }),
  });

  async function handleDeleteProfile() {
    const ok = await confirm({
      title: 'Delete Company Profile',
      message: 'This will permanently delete your company name, address, bank details, and logo. Your invoices and data will NOT be deleted. Are you sure?',
      confirmLabel: 'Yes, Delete Profile',
      danger: true,
    });
    if (!ok) return;
    deleteProfile.mutate();
  }

  const save = useMutation({
    mutationFn: () => companyApi.save(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profile'] }); qc.invalidateQueries({ queryKey: ['company-profile'] }); qc.refetchQueries({ type: 'all' }); setMsg({ type: 'ok', text: 'Saved!' }); setTimeout(() => setMsg(null), 3000); },
    onError: err => setMsg({ type: 'err', text: err instanceof Error ? err.message : 'Save failed' }),
  });

  const saveSettings = useMutation({
    mutationFn: () => companyApi.saveSettings(settingsForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-settings'] }); qc.refetchQueries({ type: 'all' }); setSettingsMsg({ type: 'ok', text: 'Settings saved!' }); setTimeout(() => setSettingsMsg(null), 3000); },
    onError: err => setSettingsMsg({ type: 'err', text: err instanceof Error ? err.message : 'Save failed' }),
  });

  function set(k: keyof CompanyProfile) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm(f => ({ ...f, [k]: e.target.value }));
    };
  }

  function setBool(k: keyof CompanyProfile) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.checked }));
  }

  async function lookupPin(pin: string) {
    if (pin.length !== 6) return;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json() as [{ Status: string; PostOffice?: { District: string; State: string }[] }];
      if (data[0]?.Status === 'Success' && data[0].PostOffice?.length) {
        const po = data[0].PostOffice[0];
        setForm(f => ({ ...f, city: f.city || po.District, state: f.state || po.State }));
      }
    } catch { /* silently fail */ }
  }

  async function saveSeqPrefix(docType: string) {
    const prefix = (seqPrefixes[docType] ?? '').trim().toUpperCase();
    if (!prefix) return;
    setSeqMsg(null);
    try {
      await companyApi.updateSequence(docType, { prefix });
      qc.invalidateQueries({ queryKey: ['sequences'] });
      qc.refetchQueries({ type: 'all' });
      setSeqMsg({ type: 'ok', text: `${DOC_TYPE_LABELS[docType] ?? docType} prefix saved!` });
      setTimeout(() => setSeqMsg(null), 3000);
    } catch (err) { setSeqMsg({ type: 'err', text: err instanceof Error ? err.message : 'Save failed' }); }
  }

  async function resetSeqCounter(docType: string) {
    if (!await confirm({ message: `Reset ${DOC_TYPE_LABELS[docType] ?? docType} counter to 0? Next invoice will be …/0001.` })) return;
    setSeqMsg(null);
    try {
      await companyApi.updateSequence(docType, { lastNumber: 0 });
      qc.invalidateQueries({ queryKey: ['sequences'] });
      qc.refetchQueries({ type: 'all' });
      setSeqMsg({ type: 'ok', text: `${DOC_TYPE_LABELS[docType] ?? docType} counter reset!` });
      setTimeout(() => setSeqMsg(null), 3000);
    } catch (err) { setSeqMsg({ type: 'err', text: err instanceof Error ? err.message : 'Reset failed' }); }
  }

  const logoRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    setLogoPreview(blobUrl);
    setLogoUploading(true);
    try {
      const { logoPath } = await companyApi.uploadLogo(file);
      setForm(f => ({ ...f, logoPath }));
      setMsg({ type: 'ok', text: 'Logo uploaded! Save Changes to apply.' });
      setTimeout(() => setMsg(null), 4000);
    } catch (err) {
      setLogoPreview(null);
      setMsg({ type: 'err', text: err instanceof Error ? err.message : 'Logo upload failed' });
    } finally {
      setLogoUploading(false);
      if (logoRef.current) logoRef.current.value = '';
    }
  }, []);

  const importRef = useRef<HTMLInputElement>(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupMsg, setBackupMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  async function handleExport() {
    setBackupBusy(true); setBackupMsg(null);
    try {
      await downloadBackup(msg => setBackupMsg({ type: 'ok', text: msg }));
      setBackupMsg({ type: 'ok', text: 'Download started!' });
    }
    catch (err) { setBackupMsg({ type: 'err', text: err instanceof Error ? err.message : 'Export failed' }); }
    finally { setBackupBusy(false); }
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true); setBackupMsg(null);
    const token = sessionStorage.getItem('cl_token');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch(`${API_BASE}/import/offline-export`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`Import failed (${res.status})`);
      const data = await res.json() as { importedFrom?: string; imported?: Record<string, number> };
      const summary = Object.entries(data.imported ?? {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      setBackupMsg({ type: 'ok', text: `Imported from "${data.importedFrom}": ${summary}` });
    } catch (err) { setBackupMsg({ type: 'err', text: err instanceof Error ? err.message : 'Import failed' }); }
    finally { setImportBusy(false); if (importRef.current) importRef.current.value = ''; }
  }

  if (isLoading) return <div className="loading-overlay"><span className="spinner" /></div>;

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <div className="page-header">
        <h1 className="page-title">Company Settings</h1>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '💾 Save Changes'}
        </button>
      </div>

      {msg && <div className={msg.type === 'ok' ? 'success-box' : 'error-box'}>{msg.text}</div>}

      {/* Business info */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Business Information</span></div>
        <div className="card-body">
          <div className="form-row form-row-2">
            <label>Business Name *<input value={form.name} onChange={set('name')} placeholder="Acme Traders Pvt Ltd" /></label>
            <label>GSTIN<input value={form.gstin ?? ''} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, gstin: v, ...(v.length === 15 && !f.pan ? { pan: v.slice(2, 12) } : {}) })); }} placeholder="22AAAAA0000A1Z5" maxLength={15} /></label>
          </div>
          <div className="form-row form-row-3">
            <label>Nature of Business
              <select value={form.natureOfBusiness ?? ''} onChange={e => setForm(f => ({ ...f, natureOfBusiness: e.target.value as CompanyProfile['natureOfBusiness'] || undefined }))}>
                <option value="">— Select —</option>
                <option value="retailer">Retailer</option>
                <option value="wholesaler">Wholesaler</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="service">Service Provider</option>
                <option value="transport">Transport / Logistics</option>
              </select>
            </label>
            <label>PAN<input value={form.pan ?? ''} onChange={set('pan')} placeholder="AAAAA0000A" maxLength={10} /></label>
            <label>CIN (Pvt Ltd / LLP)<input value={form.cin ?? ''} onChange={e => setForm(f => ({ ...f, cin: e.target.value.toUpperCase() }))} placeholder="U12345MH2020PTC123456" maxLength={21} /></label>
          </div>
          <div className="form-row form-row-4">
            <label>Email<input type="email" value={form.email ?? ''} onChange={set('email')} placeholder="billing@company.com" /></label>
            <label>Alt. Email<input type="email" value={form.altEmail ?? ''} onChange={set('altEmail')} placeholder="accounts@company.com" /></label>
            <label>Phone<input value={form.phone ?? ''} onChange={set('phone')} placeholder="+91 9876543210" /></label>
            <label>Alt. Phone<input value={form.altPhone ?? ''} onChange={set('altPhone')} placeholder="+91 9876543210" /></label>
          </div>

          <div className="form-row">
            <label>Company Logo
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={form.logoPath ?? ''} onChange={set('logoPath')} placeholder="Paste image URL or upload a file →" style={{ flex: 1, minWidth: 220 }} />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => logoRef.current?.click()} disabled={logoUploading} style={{ whiteSpace: 'nowrap' }}>
                  {logoUploading ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '📁 Upload File'}
                </button>
                <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleLogoUpload} />
              </div>
              {(logoPreview || form.logoPath) && (
                <div style={{ marginTop: '0.5rem' }}>
                  <img src={logoPreview ?? form.logoPath} alt="Logo preview" style={{ maxHeight: 56, maxWidth: 200, borderRadius: 4, border: '1px solid var(--border)', objectFit: 'contain' }} onError={e => { if (!logoPreview) (e.currentTarget.style.display = 'none'); }} />
                </div>
              )}
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 'normal' }}>
              <input type="checkbox" checked={form.isGstRegistered ?? true} onChange={setBool('isGstRegistered')} style={{ width: 'auto' }} />
              GST Registered
            </label>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 'normal' }}>
              <input type="checkbox" checked={form.isCompositionScheme ?? false} onChange={setBool('isCompositionScheme')} style={{ width: 'auto' }} />
              Composition Scheme
            </label>
          </div>

          {form.isCompositionScheme && (
            <div className="form-row" style={{ maxWidth: 240 }}>
              <label>Composition GST Rate
                <select value={form.compositionGstRate ?? 1} onChange={e => setForm(f => ({ ...f, compositionGstRate: Number(e.target.value) }))}>
                  {GST_RATES.map(r => <option key={r} value={r}>{r}% — {r === 1 ? 'Traders' : r === 2 ? 'Manufacturers' : 'Restaurants'}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Address */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Address</span></div>
        <div className="card-body">
          <div className="form-row"><label>Address Line 1<input value={form.addressLine1 ?? ''} onChange={set('addressLine1')} placeholder="Street address" /></label></div>
          <div className="form-row"><label>Address Line 2<input value={form.addressLine2 ?? ''} onChange={set('addressLine2')} placeholder="Area, landmark" /></label></div>
          <div className="form-row form-row-4">
            <label>City<input value={form.city ?? ''} onChange={set('city')} placeholder="Mumbai" /></label>
            <label>State
              <select value={form.state ?? ''} onChange={set('state')}>
                <option value="">— Select —</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>PIN Code<input value={form.postalCode ?? ''} onChange={e => { set('postalCode')(e); lookupPin(e.target.value); }} placeholder="400001" maxLength={6} /></label>
            <label>Country<input value={form.country} onChange={set('country')} placeholder="India" /></label>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Bank Details</span></div>
        <div className="card-body">
          <div className="form-row form-row-2">
            <label>Bank Name<input value={form.bankName ?? ''} onChange={set('bankName')} placeholder="HDFC Bank" /></label>
            <label>Account Name<input value={form.accountName ?? ''} onChange={set('accountName')} placeholder="Acme Traders Pvt Ltd" /></label>
          </div>
          <div className="form-row form-row-3">
            <label>Account Number<input value={form.accountNumber ?? ''} onChange={set('accountNumber')} placeholder="12345678901234" /></label>
            <label>IFSC Code<input value={form.ifscCode ?? ''} onChange={set('ifscCode')} placeholder="HDFC0001234" /></label>
            <label>UPI ID<input value={form.upiId ?? ''} onChange={set('upiId')} placeholder="business@upi" /></label>
          </div>
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Backup & Restore</span></div>
        <div className="card-body">
          {backupMsg && <div className={backupMsg.type === 'ok' ? 'success-box' : 'error-box'} style={{ marginBottom: '1rem' }}>{backupMsg.text}</div>}
          <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
            <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Export All Data</div>
              <div style={{ fontSize: '0.825rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>Download a ZIP file containing all your invoices, parties, products, expenses, and payments.</div>
              <button className="btn btn-primary btn-sm" onClick={handleExport} disabled={backupBusy}>
                {backupBusy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '⬇ Download Backup'}
              </button>
            </div>
            <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '1rem', border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Import from Desktop App</div>
              <div style={{ fontSize: '0.825rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>Upload a ZIP export from the CoreLedger desktop app to migrate your offline data to the cloud.</div>
              <button className="btn btn-ghost btn-sm" onClick={() => importRef.current?.click()} disabled={importBusy}>
                {importBusy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '⬆ Import ZIP'}
              </button>
              <input ref={importRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={handleImport} />
            </div>
          </div>
        </div>
      </div>
      {/* App Settings */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <span style={{ fontWeight: 700 }}>App Settings</span>
          <button className="btn btn-primary btn-sm" onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending}>
            {saveSettings.isPending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Save Settings'}
          </button>
        </div>
        <div className="card-body">
          {settingsMsg && <div className={settingsMsg.type === 'ok' ? 'success-box' : 'error-box'} style={{ marginBottom: '1rem' }}>{settingsMsg.text}</div>}
          <div className="form-row form-row-3">
            <label>Currency
              <select value={settingsForm.currency} onChange={e => setSettingsForm(f => ({ ...f, currency: e.target.value }))}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Date Format
              <select value={settingsForm.dateFormat} onChange={e => setSettingsForm(f => ({ ...f, dateFormat: e.target.value }))}>
                {DATE_FORMATS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label>Default Tax Type
              <select value={settingsForm.defaultTaxType} onChange={e => setSettingsForm(f => ({ ...f, defaultTaxType: e.target.value as AppSettings['defaultTaxType'] }))}>
                <option value="cgst_sgst">CGST + SGST (intra-state)</option>
                <option value="igst">IGST (inter-state)</option>
              </select>
            </label>
          </div>
          <div className="form-row form-row-2">
            <label>Default Document Type
              <select value={settingsForm.defaultDocType} onChange={e => setSettingsForm(f => ({ ...f, defaultDocType: e.target.value }))}>
                {DOC_TYPES.filter(d => d.value !== 'lorry_receipt' || isTransport).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
              <input type="checkbox" checked={settingsForm.enableStock} onChange={e => setSettingsForm(f => ({ ...f, enableStock: e.target.checked }))} style={{ width: 'auto', marginTop: 0 }} />
              <span>Enable Stock Management (deduct stock on invoice)</span>
            </label>
          </div>
          <div className="form-row">
            <label>Default Terms & Conditions (auto-fills on new invoices)
              <textarea value={settingsForm.termsAndConditions ?? ''} onChange={e => setSettingsForm(f => ({ ...f, termsAndConditions: e.target.value }))} rows={3} placeholder="Payment due within 30 days. Goods once sold will not be returned…" style={{ resize: 'vertical' }} />
            </label>
          </div>
          <div className="form-row">
            <label>Invoice Footer Note
              <input value={settingsForm.invoiceFooterNote ?? ''} onChange={e => setSettingsForm(f => ({ ...f, invoiceFooterNote: e.target.value }))} placeholder="Thank you for your business! | E. & O.E." />
            </label>
          </div>
        </div>
      </div>

      {/* Watermark */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Invoice Watermark</span></div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {['', 'ORIGINAL', 'DUPLICATE', 'TRIPLICATE', 'DRAFT', 'CANCELLED'].map(w => (
              <button key={w} type="button"
                onClick={() => { setWatermark(w); localStorage.setItem('cl_watermark', w); }}
                style={{
                  padding: '0.45rem 0.9rem', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${watermark === w ? 'var(--primary)' : 'var(--border)'}`,
                  background: watermark === w ? 'var(--primary-light)' : 'var(--surface)',
                  color: watermark === w ? 'var(--primary)' : 'var(--text-2)',
                  fontWeight: watermark === w ? 700 : 500, fontSize: '0.8rem',
                }}>{w === '' ? 'None' : w}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <input value={watermark} onChange={e => { setWatermark(e.target.value); localStorage.setItem('cl_watermark', e.target.value); }} placeholder="Custom text (e.g. COPY)" style={{ maxWidth: 240 }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Appears diagonally on printed/downloaded invoices</span>
          </div>
        </div>
      </div>

      {/* Invoice Number Series */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Invoice Number Series</span></div>
        <div className="card-body">
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Prefix appears before the number: <strong>INV/2526/0001</strong>. Changing the prefix only affects new documents.
          </p>
          {seqMsg && <div className={seqMsg.type === 'ok' ? 'success-box' : 'error-box'} style={{ marginBottom: '0.75rem' }}>{seqMsg.text}</div>}
          <table style={{ width: '100%', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>Document Type</th>
                <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>Prefix</th>
                <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-muted)', fontWeight: 600 }}>Last #</th>
                <th style={{ width: 180 }}></th>
              </tr>
            </thead>
            <tbody>
              {sequences.filter((seq: InvoiceSequenceDto) => seq.docType !== 'LORRY_RECEIPT' || isTransport).map((seq: InvoiceSequenceDto) => (
                <tr key={seq.docType} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem' }}>{DOC_TYPE_LABELS[seq.docType] ?? seq.docType}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <input
                      value={seqPrefixes[seq.docType] ?? seq.prefix}
                      onChange={e => setSeqPrefixes(p => ({ ...p, [seq.docType]: e.target.value.toUpperCase() }))}
                      maxLength={6}
                      style={{ width: 80, padding: '0.3rem 0.5rem', fontFamily: 'monospace', fontWeight: 600, textTransform: 'uppercase' }}
                    />
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-2)' }}>{seq.lastNumber}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => saveSeqPrefix(seq.docType)}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => resetSeqCounter(seq.docType)} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>Reset</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cloud Sync — desktop only */}
      {IS_DESKTOP && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><span style={{ fontWeight: 700 }}>☁ Cloud Sync</span></div>
          <div className="card-body">
            {syncError && <div className="error-box" style={{ marginBottom: '1rem' }}>{syncError}</div>}
            {conflictCount > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.65rem 1rem', marginBottom: '1rem', color: '#b91c1c', fontSize: '0.85rem' }}>
                ⚠ {conflictCount} conflict{conflictCount > 1 ? 's' : ''} detected — cloud changes overwrote {conflictCount > 1 ? 'some' : 'a'} local edit{conflictCount > 1 ? 's' : ''} that had not been pushed yet.
              </div>
            )}

            {!isConnected ? (
              <div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
                  Connect this desktop app to your CoreLedger cloud account to sync invoices, parties, products, payments, purchases, and expenses automatically.
                </p>
                <div className="form-row">
                  <label>Cloud API URL
                    <input value={cloudUrl} onChange={e => setCloudUrl(e.target.value)} placeholder="https://api.coreledger.in/v1" />
                  </label>
                </div>
                <div className="form-row form-row-2">
                  <label>Email<input type="email" value={cloudEmail} onChange={e => setCloudEmail(e.target.value)} placeholder="you@company.com" /></label>
                  <label>Password<input type="password" value={cloudPassword} onChange={e => setCloudPassword(e.target.value)} /></label>
                </div>
                <button
                  className="btn btn-primary"
                  disabled={connectingCloud || !cloudUrl || !cloudEmail || !cloudPassword}
                  onClick={async () => {
                    setConnectingCloud(true);
                    await connectToCloud(cloudUrl, cloudEmail, cloudPassword);
                    setConnectingCloud(false);
                  }}
                >
                  {connectingCloud ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🔗 Connect'}
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#22c55e', fontWeight: 600 }}>
                    <span>✓</span> Connected to cloud
                  </div>
                  {lastSyncAt && (
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      Last sync: {lastSyncAt.toLocaleString()}
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span style={{ fontSize: '0.82rem', color: '#f59e0b', fontWeight: 600 }}>
                      {pendingCount} changes pending upload
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={sync} disabled={isSyncing}>
                    {isSyncing ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '⟳ Sync Now'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={disconnect}
                    style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--danger, #dc2626)', borderWidth: 1, borderStyle: 'solid' }}>
        <div className="card-header" style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
          <span style={{ fontWeight: 700, color: '#dc2626' }}>⚠️ Danger Zone</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '0.2rem' }}>Delete Company Profile</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Permanently removes your company name, address, logo, and bank details. Your invoices and transaction data are not affected.
              </div>
            </div>
            <button
              className="btn"
              onClick={handleDeleteProfile}
              disabled={deleteProfile.isPending}
              style={{
                background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {deleteProfile.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🗑️ Delete Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Format */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Invoice Print Format</span></div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {INVOICE_FORMATS.map(f => (
              <button
                key={f.key}
                onClick={() => { setSettingsForm(s => ({ ...s, invoiceFormat: f.key })); localStorage.setItem('cl_invoice_format', f.key); }}
                style={{
                  padding: '0.65rem 1.25rem', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${settingsForm.invoiceFormat === f.key ? 'var(--primary)' : 'var(--border)'}`,
                  background: settingsForm.invoiceFormat === f.key ? 'var(--primary-light)' : 'var(--surface)',
                  color: settingsForm.invoiceFormat === f.key ? 'var(--primary)' : 'var(--text-2)',
                  fontWeight: settingsForm.invoiceFormat === f.key ? 700 : 500,
                }}
              >
                <div style={{ fontWeight: 600 }}>{f.label}</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.15rem', opacity: 0.8 }}>{f.desc}</div>
              </button>
            ))}
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Controls the print layout when downloading invoice PDFs. Click <strong>Save Settings</strong> above to persist. Current: <strong style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{settingsForm.invoiceFormat}</strong></p>
        </div>
      </div>
    </div>
  );
}
